import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  TrendingUp,
  Users,
  Building2,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar,
  Award,
  Target,
  BarChart3,
  PieChart,
  Activity,
  X,
  MapPin,
  Mail,
  Phone,
  Briefcase,
  TrendingDown
} from 'lucide-react';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  const [selectedFacility, setSelectedFacility] = useState('');
  const [facilities, setFacilities] = useState([]);
  const [summaryType, setSummaryType] = useState('unique'); // 'unique' or 'daily'
  
  const [dashboardData, setDashboardData] = useState({
    summary: {
      totalEmployees: 0,
      totalFacilities: 0,
      todayPresent: 0,
      todayAbsent: 0,
      todayLate: 0
    },
    monthAttendance: [],
    attendanceTrend: [],
    topLateComers: [],
    facilityWiseAttendance: []
  });
  
  const [performanceData, setPerformanceData] = useState([]);
  const [overtimeData, setOvertimeData] = useState([]);
  
  // Modal states
  const [employeeModal, setEmployeeModal] = useState({ isOpen: false, data: null, loading: false });
  const [facilityModal, setFacilityModal] = useState({ isOpen: false, data: null, loading: false });

  // MoM / YoY performance comparison
  const [comparisonType, setComparisonType] = useState('mom'); // 'mom' or 'yoy'
  const [comparisonMetric, setComparisonMetric] = useState('attendanceRate');
  const [comparisonSeries, setComparisonSeries] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(true);

  useEffect(() => {
    fetchFacilities();
    fetchAnalytics();
  }, [dateRange, selectedFacility, summaryType]);

  useEffect(() => {
    fetchComparisonData();
  }, [selectedFacility]);

  const fetchComparisonData = async () => {
    setComparisonLoading(true);
    try {
      const params = { months: 24, ...(selectedFacility && { facility: selectedFacility }) };
      const response = await axios.get('/api/analytics/period-comparison', { params });
      setComparisonSeries(response.data?.data?.monthly || []);
    } catch (error) {
      console.error('Failed to fetch period comparison data:', error);
      setComparisonSeries([]);
    } finally {
      setComparisonLoading(false);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch facilities:', error);
      // Don't show alert for facilities, just use empty array
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        summaryType,
        ...(selectedFacility && { facility: selectedFacility })
      };

      const [dashboardRes, performanceRes, overtimeRes] = await Promise.all([
        axios.get('/api/analytics/dashboard', { params }),
        axios.get('/api/analytics/employee-performance', { params: { ...params, limit: 10 } }),
        axios.get('/api/analytics/overtime', { params })
      ]);

      const dashboardDataRaw = dashboardRes.data.data || {
        summary: {
          totalEmployees: 0,
          totalFacilities: 0,
          todayPresent: 0,
          todayAbsent: 0,
          todayLate: 0,
          attendanceRate: 0,
          punctualityRate: 0,
          todayWorkHours: 0,
          todayOnTime: 0
        },
        monthAttendance: [],
        attendanceTrend: [],
        topLateComers: [],
        facilityWiseAttendance: []
      };
      
      // Filter out attendance trends with invalid dates
      if (dashboardDataRaw.attendanceTrend && Array.isArray(dashboardDataRaw.attendanceTrend)) {
        dashboardDataRaw.attendanceTrend = dashboardDataRaw.attendanceTrend.filter(day => {
          return day && day.date && !isNaN(new Date(day.date).getTime());
        });
      }

      // Ensure all required arrays exist
      dashboardDataRaw.monthAttendance = dashboardDataRaw.monthAttendance || [];
      dashboardDataRaw.topLateComers = dashboardDataRaw.frequentLateArrivals || [];
      dashboardDataRaw.facilityWiseAttendance = dashboardDataRaw.facilityWisePerformance || [];
      dashboardDataRaw.attendanceTrend = dashboardDataRaw.attendanceTrend || [];

      setDashboardData(dashboardDataRaw);
      setPerformanceData(performanceRes.data.data || []);
      setOvertimeData(overtimeRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      alert('Failed to load analytics data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const calculateAttendanceRate = () => {
    // Use the backend-calculated attendance rate if available, otherwise calculate manually
    if (dashboardData?.summary?.attendanceRate !== undefined) {
      return dashboardData.summary.attendanceRate;
    }
    
    // Fallback to manual calculation
    const total = dashboardData?.summary?.totalEmployees || 0;
    if (total === 0) return 0;
    const present = dashboardData?.summary?.todayPresent || 0;
    return ((present / total) * 100).toFixed(1);
  };

  const calculatePunctualityRate = () => {
    // Use the backend-calculated punctuality rate if available
    if (dashboardData?.summary?.punctualityRate !== undefined) {
      return dashboardData.summary.punctualityRate;
    }
    
    // Fallback: Calculate punctuality rate (on-time / total arrivals)
    const totalArrivals = (dashboardData?.summary?.todayOnTime || 0) + (dashboardData?.summary?.todayLate || 0);
    if (totalArrivals === 0) return 0;
    return (((dashboardData?.summary?.todayOnTime || 0) / totalArrivals) * 100).toFixed(1);
  };

  const getMonthStats = () => {
    const stats = {
      present: 0,
      absent: 0,
      late: 0,
      onTime: 0,
      totalWorkHours: 0,
      totalOvertime: 0,
      excused: 0,
      incomplete: 0
    };

    // Use period statistics from summary (respects summaryType toggle)
    if (dashboardData?.summary) {
      stats.present = dashboardData.summary.periodPresent || 0;
      stats.absent = dashboardData.summary.periodAbsent || 0;
      stats.late = dashboardData.summary.periodLate || 0;
      stats.onTime = dashboardData.summary.periodOnTime || 0;
      stats.excused = dashboardData.summary.periodExcused || 0;
      stats.incomplete = dashboardData.summary.periodIncomplete || 0;
      
      // Fallback to monthlyStats for work hours if period stats not available
      if (dashboardData.monthlyStats) {
        stats.totalWorkHours = dashboardData.monthlyStats.totalWorkHours || 0;
        stats.totalOvertime = dashboardData.monthlyStats.totalOvertime || 0;
      }
    } else if (dashboardData?.monthlyStats) {
      // Fallback to old format if new period stats not available
      stats.present = dashboardData.monthlyStats.presentDays || 0;
      stats.absent = dashboardData.monthlyStats.absentDays || 0;
      stats.late = dashboardData.monthlyStats.lateDays || 0;
      stats.totalWorkHours = dashboardData.monthlyStats.totalWorkHours || 0;
      stats.totalOvertime = dashboardData.monthlyStats.totalOvertime || 0;
    } else if (dashboardData?.monthAttendance && Array.isArray(dashboardData.monthAttendance)) {
      // Legacy format
      dashboardData.monthAttendance.forEach(item => {
        if (item._id === 'present') stats.present = item.count;
        if (item._id === 'absent') stats.absent = item.count;
        if (item._id === 'late') stats.late = item.count;
        stats.totalWorkHours += item.totalWorkHours || 0;
        stats.totalOvertime += item.totalOvertime || 0;
      });
    }

    return stats;
  };

  // Helper function to safely format dates
  const formatDate = (date, formatString) => {
    try {
      if (!date) return '-';
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) return '-';
      return format(parsedDate, formatString);
    } catch (error) {
      console.error('Date formatting error:', error);
      return '-';
    }
  };

  // Fetch detailed employee insights
  const fetchEmployeeDetails = async (employeeId) => {
    setEmployeeModal({ isOpen: true, data: null, loading: true });
    try {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };

      const [employeeRes, attendanceRes] = await Promise.all([
        axios.get(`/api/employees/${employeeId}`),
        axios.get('/api/attendance', { 
          params: { ...params, employee: employeeId, limit: 30, sortBy: 'date', sortOrder: 'desc' }
        })
      ]);

      const employee = employeeRes.data.data;
      const attendanceRecords = (attendanceRes.data.data || []).filter(record => {
        // Filter out records with invalid dates
        return record && record.date && !isNaN(new Date(record.date).getTime());
      });

      // Calculate detailed metrics
      const metrics = {
        totalRecords: attendanceRecords.length,
        presentCount: attendanceRecords.filter(a => a.status === 'present').length,
        lateCount: attendanceRecords.filter(a => a.status === 'late').length,
        absentCount: attendanceRecords.filter(a => a.status === 'absent').length,
        totalWorkHours: attendanceRecords.reduce((sum, a) => sum + (a.workHours || 0), 0),
        totalOvertime: attendanceRecords.reduce((sum, a) => sum + (a.overtime || 0), 0),
        avgWorkHours: 0,
        avgLateMinutes: 0,
        recentAttendance: attendanceRecords.slice(0, 10)
      };

      metrics.avgWorkHours = metrics.totalRecords > 0 
        ? (metrics.totalWorkHours / metrics.totalRecords).toFixed(2) 
        : 0;

      const lateRecords = attendanceRecords.filter(a => a.lateMinutes > 0);
      metrics.avgLateMinutes = lateRecords.length > 0
        ? (lateRecords.reduce((sum, a) => sum + a.lateMinutes, 0) / lateRecords.length).toFixed(0)
        : 0;

      setEmployeeModal({ isOpen: true, data: { employee, metrics }, loading: false });
    } catch (error) {
      console.error('Failed to fetch employee details:', error);
      alert('Failed to load employee details. Please try again.');
      setEmployeeModal({ isOpen: false, data: null, loading: false });
    }
  };

  // Fetch detailed facility insights
  const fetchFacilityDetails = async (facilityId) => {
    setFacilityModal({ isOpen: true, data: null, loading: true });
    try {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        facility: facilityId
      };

      const [facilityRes, dashboardRes, employeesRes] = await Promise.all([
        axios.get(`/api/facilities/${facilityId}`),
        axios.get('/api/analytics/dashboard', { params }),
        axios.get('/api/analytics/employee-performance', { params: { ...params, limit: 5 } })
      ]);

      const facility = facilityRes.data.data;
      const analytics = dashboardRes.data.data;
      const topEmployees = employeesRes.data.data || [];

      // Filter out attendance trends with invalid dates
      if (analytics.attendanceTrend) {
        analytics.attendanceTrend = analytics.attendanceTrend.filter(day => {
          return day && day.date && !isNaN(new Date(day.date).getTime());
        });
      }

      setFacilityModal({ 
        isOpen: true, 
        data: { facility, analytics, topEmployees }, 
        loading: false 
      });
    } catch (error) {
      console.error('Failed to fetch facility details:', error);
      alert('Failed to load facility details. Please try again.');
      setFacilityModal({ isOpen: false, data: null, loading: false });
    }
  };

  const monthStats = getMonthStats();
  const attendanceRate = calculateAttendanceRate();
  const punctualityRate = calculatePunctualityRate();
  const todayWorkHours = dashboardData?.summary?.todayWorkHours || 0;

  // MoM / YoY comparison derived views - comparisonSeries is chronological ascending,
  // last entry is the current month. MoM needs 2 months; YoY needs 13 (this month + the
  // same month 12 back) and up to 24 for the full overlay chart (this year vs last year).
  const COMPARISON_METRICS = {
    attendanceRate: { label: 'Attendance Rate', unit: '%', higherIsBetter: true, color: '#8b5cf6' },
    punctualityRate: { label: 'Punctuality Rate', unit: '%', higherIsBetter: true, color: '#6366f1' },
    lateDays: { label: 'Late Count', unit: '', higherIsBetter: false, color: '#f59e0b' },
    absentDays: { label: 'Absent Count', unit: '', higherIsBetter: false, color: '#ef4444' },
    totalWorkHours: { label: 'Work Hours', unit: 'hrs', higherIsBetter: true, color: '#06b6d4' },
    totalOvertime: { label: 'Overtime', unit: 'hrs', higherIsBetter: true, color: '#10b981' }
  };

  const computeChange = (current, previous) => {
    if (current == null || previous == null) return null;
    const diff = Math.round((current - previous) * 100) / 100;
    const pct = previous !== 0 ? Math.round((diff / previous) * 1000) / 10 : (current !== 0 ? 100 : 0);
    return { diff, pct };
  };

  const comparisonCurrent = comparisonSeries[comparisonSeries.length - 1] || null;
  const comparisonPrevious = comparisonType === 'mom'
    ? comparisonSeries[comparisonSeries.length - 2] || null
    : comparisonSeries[comparisonSeries.length - 13] || null;

  const momChartData = comparisonSeries.slice(-12).map(m => ({ label: m.label, value: m[comparisonMetric] }));

  const thisYearMonths = comparisonSeries.slice(-12);
  const lastYearMonths = comparisonSeries.slice(-24, -12);
  const yoyChartData = thisYearMonths.map((m, idx) => ({
    label: m.label.split(' ')[0],
    thisYear: m[comparisonMetric],
    lastYear: lastYearMonths[idx] ? lastYearMonths[idx][comparisonMetric] : null
  }));

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Analytics & Insights
            </h1>
            <p className="text-gray-600 mt-1">Advanced attendance analytics and performance metrics</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <select
            className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
          >
            <option value="">All Facilities</option>
            {facilities.map(facility => (
              <option key={facility._id} value={facility._id}>
                {facility.name}
              </option>
            ))}
          </select>

          <div className="flex gap-2">
            <input
              type="date"
              className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            />
            <input
              type="date"
              className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
        </div>
      </div>
      
      {/* Summary Type Toggle */}
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">Summary Calculation Method</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => setSummaryType('unique')}
            className={`p-3 rounded-lg border-2 transition-all text-left ${
              summaryType === 'unique'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 hover:border-gray-300 text-gray-700'
            }`}
          >
            <div className="font-medium mb-1">Unique Employees</div>
            <div className="text-xs opacity-75">
              Count each employee once per status (for HR review)
            </div>
          </button>
          <button
            onClick={() => setSummaryType('daily')}
            className={`p-3 rounded-lg border-2 transition-all text-left ${
              summaryType === 'daily'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 hover:border-gray-300 text-gray-700'
            }`}
          >
            <div className="font-medium mb-1">Daily Average</div>
            <div className="text-xs opacity-75">
              Average attendance per day (for staffing decisions)
            </div>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-12 w-12 text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600">Loading analytics data...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 sm:gap-4 lg:gap-6">
            <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-3 sm:p-4 lg:p-6 border border-gray-100 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-blue-100 text-xs sm:text-sm font-medium">Total Employees</p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 sm:mt-2">{dashboardData?.summary?.totalEmployees || 0}</p>
                </div>
                <Users className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-blue-200 flex-shrink-0 ml-2" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-3 sm:p-4 lg:p-6 border border-gray-100 bg-gradient-to-br from-green-500 to-green-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-green-100 text-xs sm:text-sm font-medium">Today Present</p>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold mt-1 sm:mt-2">{dashboardData?.summary?.todayPresent || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-green-200 flex-shrink-0 ml-2" />
              </div>
            </div>

            <div className="card bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium">Today Late</p>
                  <p className="text-3xl font-bold mt-2">{dashboardData?.summary?.todayLate || 0}</p>
                </div>
                <Clock className="w-12 h-12 text-orange-200" />
              </div>
            </div>

            <div className="card bg-gradient-to-br from-red-500 to-red-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm font-medium">Today Absent</p>
                  <p className="text-3xl font-bold mt-2">{dashboardData?.summary?.todayAbsent || 0}</p>
                </div>
                <XCircle className="w-12 h-12 text-red-200" />
              </div>
            </div>

            <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">Today's Attendance Rate</p>
                  <p className="text-3xl font-bold mt-2">{attendanceRate}%</p>
                </div>
                <Target className="w-12 h-12 text-purple-200" />
              </div>
            </div>

            <div className="card bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm font-medium">Punctuality Rate</p>
                  <p className="text-3xl font-bold mt-2">{punctualityRate}%</p>
                </div>
                <Clock className="w-12 h-12 text-indigo-200" />
              </div>
            </div>

            <div className="card bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-100 text-sm font-medium">Today Work Hours</p>
                  <p className="text-3xl font-bold mt-2">{todayWorkHours.toFixed(1)}</p>
                </div>
                <Clock className="w-12 h-12 text-cyan-200" />
              </div>
            </div>
          </div>

          {/* Month Statistics */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-blue-600" />
              Month Statistics {summaryType === 'unique' ? '(Unique Employees)' : '(Daily Average)'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">{summaryType === 'unique' ? 'Present' : 'Avg Present'}</p>
                <p className="text-2xl font-bold text-green-600">{monthStats.present}</p>
              </div>
              <div className="text-center p-4 bg-teal-50 rounded-lg">
                <p className="text-sm text-gray-600">{summaryType === 'unique' ? 'On-time' : 'Avg On-time'}</p>
                <p className="text-2xl font-bold text-teal-600">{monthStats.onTime}</p>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-gray-600">{summaryType === 'unique' ? 'Late' : 'Avg Late'}</p>
                <p className="text-2xl font-bold text-orange-600">{monthStats.late}</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600">{summaryType === 'unique' ? 'Absent' : 'Avg Absent'}</p>
                <p className="text-2xl font-bold text-red-600">{monthStats.absent}</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Total Work Hours</p>
                <p className="text-2xl font-bold text-blue-600">{monthStats.totalWorkHours.toFixed(0)}</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600">Total Overtime</p>
                <p className="text-2xl font-bold text-purple-600">{monthStats.totalOvertime.toFixed(0)}</p>
              </div>
            </div>
          </div>

          {/* Performance Trends - Month-over-Month / Year-over-Year */}
          <div className="card">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-blue-600" />
                Performance Trends
              </h2>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    onClick={() => setComparisonType('mom')}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      comparisonType === 'mom' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Month-over-Month
                  </button>
                  <button
                    onClick={() => setComparisonType('yoy')}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      comparisonType === 'yoy' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Year-over-Year
                  </button>
                </div>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={comparisonMetric}
                  onChange={(e) => setComparisonMetric(e.target.value)}
                >
                  {Object.entries(COMPARISON_METRICS).map(([key, cfg]) => (
                    <option key={key} value={key}>{cfg.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {comparisonLoading ? (
              <div className="flex items-center justify-center h-64 text-gray-500">Loading trend data...</div>
            ) : comparisonSeries.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-500">No attendance history available yet for this view.</div>
            ) : (
              <>
                {/* Current vs comparison period summary */}
                {comparisonCurrent && comparisonPrevious ? (() => {
                  const cfg = COMPARISON_METRICS[comparisonMetric];
                  const change = computeChange(comparisonCurrent[comparisonMetric], comparisonPrevious[comparisonMetric]);
                  const improved = change && (cfg.higherIsBetter ? change.diff >= 0 : change.diff <= 0);
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">
                          {comparisonType === 'mom' ? 'Previous Month' : 'Same Month Last Year'} ({comparisonPrevious.label})
                        </p>
                        <p className="text-2xl font-bold text-gray-700 mt-1">
                          {comparisonPrevious[comparisonMetric]}{cfg.unit}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-gray-600">Current Month ({comparisonCurrent.label})</p>
                        <p className="text-2xl font-bold text-blue-700 mt-1">
                          {comparisonCurrent[comparisonMetric]}{cfg.unit}
                        </p>
                      </div>
                      <div className={`text-center p-4 rounded-lg ${improved ? 'bg-green-50' : 'bg-red-50'}`}>
                        <p className="text-sm text-gray-600">Change</p>
                        <p className={`text-2xl font-bold mt-1 flex items-center justify-center gap-1 ${improved ? 'text-green-600' : 'text-red-600'}`}>
                          {change && (change.diff >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />)}
                          {change ? `${change.diff >= 0 ? '+' : ''}${change.diff}${cfg.unit}` : 'N/A'}
                        </p>
                      </div>
                    </div>
                  );
                })() : (
                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                    Not enough attendance history yet for a {comparisonType === 'mom' ? 'month-over-month' : 'year-over-year'} comparison
                    {comparisonType === 'yoy' ? ' (needs at least 13 months of data)' : ''}.
                  </div>
                )}

                {/* Trend chart */}
                <div className="h-64 sm:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    {comparisonType === 'mom' ? (
                      <LineChart data={momChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="label" fontSize={12} tick={{ fill: '#64748b' }} />
                        <YAxis fontSize={12} tick={{ fill: '#64748b' }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                          formatter={(value) => [`${value}${COMPARISON_METRICS[comparisonMetric].unit}`, COMPARISON_METRICS[comparisonMetric].label]}
                        />
                        <Line type="monotone" dataKey="value" name={COMPARISON_METRICS[comparisonMetric].label} stroke={COMPARISON_METRICS[comparisonMetric].color} strokeWidth={3} dot={{ r: 4 }} />
                      </LineChart>
                    ) : (
                      <LineChart data={yoyChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="label" fontSize={12} tick={{ fill: '#64748b' }} />
                        <YAxis fontSize={12} tick={{ fill: '#64748b' }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                          formatter={(value, name) => [`${value}${COMPARISON_METRICS[comparisonMetric].unit}`, name]}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="thisYear" name="This Year" stroke={COMPARISON_METRICS[comparisonMetric].color} strokeWidth={3} dot={{ r: 4 }} connectNulls />
                        <Line type="monotone" dataKey="lastYear" name="Last Year" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} connectNulls />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </div>

                {/* All-metrics summary table */}
                {comparisonCurrent && comparisonPrevious && (
                  <div className="overflow-x-auto mt-6">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Metric</th>
                          <th>{comparisonPrevious.label}</th>
                          <th>{comparisonCurrent.label}</th>
                          <th>Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(COMPARISON_METRICS).map(([key, cfg]) => {
                          const change = computeChange(comparisonCurrent[key], comparisonPrevious[key]);
                          const improved = change && (cfg.higherIsBetter ? change.diff >= 0 : change.diff <= 0);
                          return (
                            <tr key={key}>
                              <td className="font-medium">{cfg.label}</td>
                              <td className="text-gray-500">{comparisonPrevious[key]}{cfg.unit}</td>
                              <td className="font-medium">{comparisonCurrent[key]}{cfg.unit}</td>
                              <td className={improved ? 'text-green-600' : 'text-red-600'}>
                                {change ? `${change.diff >= 0 ? '+' : ''}${change.diff}${cfg.unit}` : 'N/A'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Top Performers and Late Comers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Performers */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Award className="w-6 h-6 text-green-600" />
                Top Performers
              </h2>
              <div className="space-y-3">
                {performanceData.slice(0, 5).map((item, index) => (
                  <div 
                    key={item.employee._id} 
                    onClick={() => fetchEmployeeDetails(item.employee._id)}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-blue-50 hover:shadow-md transition cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'bg-yellow-400 text-white' :
                        index === 1 ? 'bg-gray-300 text-white' :
                        index === 2 ? 'bg-orange-400 text-white' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          {item.employee.firstName} {item.employee.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{item.employee.employeeId}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">{item.metrics.punctualityScore}%</p>
                      <p className="text-xs text-gray-500">{item.metrics.presentDays - item.metrics.lateDays}/{item.metrics.totalDays} on-time</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Late Comers */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-orange-600" />
                Frequent Late Arrivals
              </h2>
              <div className="space-y-3">
                {(dashboardData?.topLateComers || []).map((item, index) => (
                  <div 
                    key={item.employee._id} 
                    onClick={() => fetchEmployeeDetails(item.employee._id)}
                    className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 hover:shadow-md transition cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          {item.employee.firstName} {item.employee.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{item.employee.employeeId}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-orange-600">{item.lateCount}x</p>
                      <p className="text-xs text-gray-500">Avg: {item.avgLateMinutes?.toFixed(0)} min</p>
                    </div>
                  </div>
                ))}
                {(dashboardData?.topLateComers || []).length === 0 && (
                  <p className="text-gray-500 text-center py-4">No late arrivals this period! 🎉</p>
                )}
              </div>
            </div>
          </div>

          {/* Facility-wise Performance */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-blue-600" />
              Facility-wise Performance
            </h2>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Facility</th>
                    <th>Code</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>Total Hours</th>
                    <th>Attendance Rate (Period)</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboardData?.facilityWiseAttendance || []).map(item => (
                    <tr 
                      key={item.facility._id}
                      onClick={() => fetchFacilityDetails(item.facility._id)}
                      className="hover:bg-blue-50 cursor-pointer transition"
                    >
                      <td className="font-medium">{item.facility.name}</td>
                      <td className="text-gray-500">{item.facility.code}</td>
                      <td>
                        <span className="badge badge-success">{item.present}</span>
                      </td>
                      <td>
                        <span className="badge badge-error">{item.absent}</span>
                      </td>
                      <td>{item.totalWorkHours?.toFixed(0)} hrs</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${item.attendanceRate}%` }}
                            ></div>
                          </div>
                          <span className="font-semibold text-sm">{item.attendanceRate?.toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Overtime Report */}
          {overtimeData.length > 0 && (
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6 text-purple-600" />
                Overtime Report
              </h2>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Employee ID</th>
                      <th>Facility</th>
                      <th>Total Overtime</th>
                      <th>Overtime Days</th>
                      <th>Avg/Day</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overtimeData.slice(0, 10).map(item => (
                      <tr key={item.employee._id}>
                        <td className="font-medium">
                          {item.employee.firstName} {item.employee.lastName}
                        </td>
                        <td className="text-gray-500">{item.employee.employeeId}</td>
                        <td className="text-gray-600">{item.facility.name}</td>
                        <td>
                          <span className="font-bold text-purple-600">{item.totalOvertime} hrs</span>
                        </td>
                        <td>{item.overtimeDays} days</td>
                        <td>{item.avgOvertimePerDay} hrs</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Employee Performance Detail */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Activity className="w-6 h-6 text-blue-600" />
              Employee Performance Details
            </h2>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>ID</th>
                    <th>Department</th>
                    <th>Total Days</th>
                    <th>Present</th>
                    <th>Late</th>
                    <th>Absent</th>
                    <th>Work Hours</th>
                    <th>Overtime</th>
                    <th>Attendance Rate (Period)</th>
                    <th>Punctuality</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceData.map(item => (
                    <tr 
                      key={item.employee._id}
                      onClick={() => fetchEmployeeDetails(item.employee._id)}
                      className="hover:bg-blue-50 cursor-pointer transition"
                    >
                      <td className="font-medium">
                        {item.employee.firstName} {item.employee.lastName}
                      </td>
                      <td className="text-gray-500">{item.employee.employeeId}</td>
                      <td className="text-gray-600">{item.employee.department}</td>
                      <td>{item.metrics.totalDays}</td>
                      <td>
                        <span className="badge badge-success">{item.metrics.presentDays}</span>
                      </td>
                      <td>
                        <span className="badge badge-warning">{item.metrics.lateDays}</span>
                      </td>
                      <td>
                        <span className="badge badge-error">{item.metrics.absentDays}</span>
                      </td>
                      <td>{item.metrics.totalWorkHours} hrs</td>
                      <td className="text-purple-600 font-semibold">{item.metrics.totalOvertime} hrs</td>
                      <td>
                        <span className={`font-bold ${
                          item.metrics.attendanceRate >= 90 ? 'text-green-600' :
                          item.metrics.attendanceRate >= 75 ? 'text-orange-600' :
                          'text-red-600'
                        }`}>
                          {item.metrics.attendanceRate}%
                        </span>
                      </td>
                      <td>
                        <span className={`font-bold ${
                          item.metrics.punctualityScore >= 90 ? 'text-green-600' :
                          item.metrics.punctualityScore >= 75 ? 'text-orange-600' :
                          'text-red-600'
                        }`}>
                          {item.metrics.punctualityScore}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Employee Details Modal */}
      {employeeModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Users className="w-6 h-6 text-blue-600" />
                Employee Details
              </h2>
              <button
                onClick={() => setEmployeeModal({ isOpen: false, data: null, loading: false })}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <XCircle className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              {employeeModal.loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner"></div>
                </div>
              ) : employeeModal.data ? (
                <>
                  {/* Employee Info */}
                  <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 mb-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">
                          {employeeModal.data.employee.firstName} {employeeModal.data.employee.lastName}
                        </h3>
                        <div className="space-y-1 text-gray-600">
                          <p className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            <span className="font-medium">ID:</span> {employeeModal.data.employee.employeeId}
                          </p>
                          <p className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            <span className="font-medium">Department:</span> {employeeModal.data.employee.department}
                          </p>
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span className="font-medium">Facility:</span> {employeeModal.data.employee.facility?.name}
                          </p>
                          {employeeModal.data.employee.email && (
                            <p className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              <span className="font-medium">Email:</span> {employeeModal.data.employee.email}
                            </p>
                          )}
                          {employeeModal.data.employee.phone && (
                            <p className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              <span className="font-medium">Phone:</span> {employeeModal.data.employee.phone}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          employeeModal.data.employee.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {employeeModal.data.employee.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Performance Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="card bg-green-50 text-center">
                      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Present</p>
                      <p className="text-2xl font-bold text-green-600">{employeeModal.data.metrics.presentCount}</p>
                    </div>
                    <div className="card bg-orange-50 text-center">
                      <Clock className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Late</p>
                      <p className="text-2xl font-bold text-orange-600">{employeeModal.data.metrics.lateCount}</p>
                    </div>
                    <div className="card bg-red-50 text-center">
                      <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Absent</p>
                      <p className="text-2xl font-bold text-red-600">{employeeModal.data.metrics.absentCount}</p>
                    </div>
                    <div className="card bg-blue-50 text-center">
                      <Activity className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Total Records</p>
                      <p className="text-2xl font-bold text-blue-600">{employeeModal.data.metrics.totalRecords}</p>
                    </div>
                  </div>

                  {/* Work Hours Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="card bg-purple-50">
                      <p className="text-sm text-gray-600 mb-1">Total Work Hours</p>
                      <p className="text-2xl font-bold text-purple-600">{employeeModal.data.metrics.totalWorkHours.toFixed(1)} hrs</p>
                    </div>
                    <div className="card bg-indigo-50">
                      <p className="text-sm text-gray-600 mb-1">Avg Work Hours/Day</p>
                      <p className="text-2xl font-bold text-indigo-600">{employeeModal.data.metrics.avgWorkHours} hrs</p>
                    </div>
                    <div className="card bg-orange-50">
                      <p className="text-sm text-gray-600 mb-1">Avg Late Minutes</p>
                      <p className="text-2xl font-bold text-orange-600">{employeeModal.data.metrics.avgLateMinutes} min</p>
                    </div>
                  </div>

                  {/* Recent Attendance */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      Recent Attendance (Last 10 Records)
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Status</th>
                            <th>Check In</th>
                            <th>Check Out</th>
                            <th>Work Hours</th>
                            <th>Overtime</th>
                            <th>Late Minutes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(employeeModal.data?.metrics?.recentAttendance || []).map(record => (
                            <tr key={record._id}>
                              <td className="font-medium">{formatDate(record.date, 'MMM dd, yyyy')}</td>
                              <td>
                                <span className={`badge ${
                                  record.status === 'present' ? 'badge-success' :
                                  record.status === 'late' ? 'badge-warning' :
                                  'badge-error'
                                }`}>
                                  {record.status}
                                </span>
                              </td>
                              <td className="text-gray-600">{formatDate(record.checkIn?.time, 'HH:mm')}</td>
                              <td className="text-gray-600">{formatDate(record.checkOut?.time, 'HH:mm')}</td>
                              <td className="font-semibold">{record.workHours?.toFixed(2) || 0} hrs</td>
                              <td className="text-purple-600">{record.overtime?.toFixed(2) || 0} hrs</td>
                              <td className={record.lateMinutes > 0 ? 'text-orange-600 font-semibold' : 'text-gray-400'}>
                                {record.lateMinutes || 0} min
                              </td>
                            </tr>
                          ))}
                          {(employeeModal.data?.metrics?.recentAttendance || []).length === 0 && (
                            <tr>
                              <td colSpan="7" className="text-center text-gray-500 py-4">
                                No attendance records found for this period
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-center text-gray-500 py-8">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Facility Details Modal */}
      {facilityModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Building2 className="w-6 h-6 text-blue-600" />
                Facility Insights
              </h2>
              <button
                onClick={() => setFacilityModal({ isOpen: false, data: null, loading: false })}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <XCircle className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              {facilityModal.loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner"></div>
                </div>
              ) : facilityModal.data ? (
                <>
                  {/* Facility Info */}
                  <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 mb-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">
                          {facilityModal.data.facility.name}
                        </h3>
                        <div className="space-y-1 text-gray-600">
                          <p className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            <span className="font-medium">Code:</span> {facilityModal.data.facility.code}
                          </p>
                          {facilityModal.data.facility.address && (
                            <p className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span className="font-medium">Address:</span> {facilityModal.data.facility.address}
                            </p>
                          )}
                          {facilityModal.data.facility.city && (
                            <p className="text-gray-600 ml-6">
                              {facilityModal.data.facility.city}, {facilityModal.data.facility.state} {facilityModal.data.facility.zipCode}
                            </p>
                          )}
                          {facilityModal.data.facility.contactPerson && (
                            <p className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              <span className="font-medium">Contact:</span> {facilityModal.data.facility.contactPerson}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          facilityModal.data.facility.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {facilityModal.data.facility.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Attendance Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="card bg-blue-50 text-center">
                      <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Total Employees</p>
                      <p className="text-2xl font-bold text-blue-600">{facilityModal.data.analytics.summary.totalEmployees}</p>
                    </div>
                    <div className="card bg-green-50 text-center">
                      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Today Present</p>
                      <p className="text-2xl font-bold text-green-600">{facilityModal.data.analytics.summary.todayPresent}</p>
                    </div>
                    <div className="card bg-orange-50 text-center">
                      <Clock className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Today Late</p>
                      <p className="text-2xl font-bold text-orange-600">{facilityModal.data.analytics.summary.todayLate}</p>
                    </div>
                    <div className="card bg-red-50 text-center">
                      <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Today Absent</p>
                      <p className="text-2xl font-bold text-red-600">{facilityModal.data.analytics.summary.todayAbsent}</p>
                    </div>
                  </div>

                  {/* Top Performers at this Facility */}
                  <div className="card mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Award className="w-5 h-5 text-green-600" />
                      Top Performers at This Facility
                    </h3>
                    <div className="space-y-3">
                      {(facilityModal.data?.topEmployees || []).map((item, index) => (
                        <div 
                          key={item.employee._id}
                          onClick={() => {
                            setFacilityModal({ isOpen: false, data: null, loading: false });
                            fetchEmployeeDetails(item.employee._id);
                          }}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                              index === 0 ? 'bg-yellow-400 text-white' :
                              index === 1 ? 'bg-gray-300 text-white' :
                              index === 2 ? 'bg-orange-400 text-white' :
                              'bg-gray-200 text-gray-600'
                            }`}>
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">
                                {item.employee.firstName} {item.employee.lastName}
                              </p>
                              <p className="text-sm text-gray-500">{item.employee.employeeId}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">{item.metrics.attendanceRate}%</p>
                            <p className="text-xs text-gray-500">{item.metrics.presentDays}/{item.metrics.totalDays} days</p>
                          </div>
                        </div>
                      ))}
                      {(facilityModal.data?.topEmployees || []).length === 0 && (
                        <p className="text-gray-500 text-center py-4">No employee data available</p>
                      )}
                    </div>
                  </div>

                  {/* Attendance Trend */}
                  {(facilityModal.data?.analytics?.attendanceTrend || []).length > 0 && (
                    <div className="card">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        7-Day Attendance Trend
                      </h3>
                      <div className="space-y-2">
                        {(facilityModal.data?.analytics?.attendanceTrend || []).map(day => {
                          const total = day.present + day.absent + day.late;
                          const presentPercentage = total > 0 ? ((day.present + day.late) / total * 100).toFixed(1) : 0;
                          
                          return (
                            <div key={day.date} className="flex items-center gap-4">
                              <div className="w-24 text-sm text-gray-600 font-medium">
                                {formatDate(day.date, 'MMM dd')}
                              </div>
                              <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                                <div
                                  className="bg-gradient-to-r from-green-400 to-green-600 h-6 rounded-full flex items-center justify-center"
                                  style={{ width: `${presentPercentage}%` }}
                                >
                                  {presentPercentage > 10 && (
                                    <span className="text-xs font-bold text-white">{presentPercentage}%</span>
                                  )}
                                </div>
                              </div>
                              <div className="text-sm text-gray-600 w-32">
                                {day.present + day.late}/{total} present
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center text-gray-500 py-8">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
